/** Curated remote model dimensions — mirrors backend remote-capabilities.ts */
const REMOTE_MODEL_DIMENSIONS: Record<string, number[]> = {
  'text-embedding-3-small': [512, 1536],
  'text-embedding-3-large': [256, 1024, 3072],
  'text-embedding-ada-002': [1536],
  'nvidia/nv-embedqa-e5-v5': [1024],
  'nomic-ai/nomic-embed-text-v1.5': [64, 128, 256, 512, 768],
  'sentence-transformers/all-MiniLM-L6-v2': [384],
  'Qwen/Qwen3-Embedding-0.6B': [256, 512, 1024],
  'Qwen/Qwen3-Embedding-4B': [256, 512, 1024, 1536, 2048, 2560],
  'Qwen/Qwen3-Embedding-8B': [256, 512, 1024, 1536, 2048, 4096],
  'qwen/qwen3-embedding-0.6b': [256, 512, 1024],
  'qwen/qwen3-embedding-4b': [256, 512, 1024, 1536, 2048, 2560],
  'qwen/qwen3-embedding-8b': [256, 512, 1024, 1536, 2048, 4096],
}

/** Preferred request size when the endpoint supports Matryoshka / variable dims. */
const REMOTE_MODEL_RECOMMENDED: Record<string, number> = {
  'text-embedding-3-small': 1536,
  'text-embedding-3-large': 1024,
  'text-embedding-ada-002': 1536,
  'nvidia/nv-embedqa-e5-v5': 1024,
  'nomic-ai/nomic-embed-text-v1.5': 512,
  'sentence-transformers/all-MiniLM-L6-v2': 384,
  'Qwen/Qwen3-Embedding-0.6B': 1024,
  'Qwen/Qwen3-Embedding-4B': 2560,
  'Qwen/Qwen3-Embedding-8B': 4096,
  'qwen/qwen3-embedding-0.6b': 1024,
  'qwen/qwen3-embedding-4b': 2560,
  'qwen/qwen3-embedding-8b': 4096,
}

function matchQwenEmbeddingFamily(model: string): '0.6b' | '4b' | '8b' | null {
  const m = model.toLowerCase()
  if (!/qwen/.test(m) || !/embed/.test(m)) return null
  if (/0\.?6\s*b|0_6b/.test(m)) return '0.6b'
  if (/8\s*b/.test(m)) return '8b'
  if (/4\s*b/.test(m)) return '4b'
  return null
}

export function listRemoteModelDimensions(model: string): number[] {
  const exact = REMOTE_MODEL_DIMENSIONS[model]
  if (exact) return exact
  if (/nomic-embed-text-v1\.5/i.test(model)) {
    return REMOTE_MODEL_DIMENSIONS['nomic-ai/nomic-embed-text-v1.5'] ?? [512]
  }
  const qwen = matchQwenEmbeddingFamily(model)
  if (qwen === '0.6b') return REMOTE_MODEL_DIMENSIONS['Qwen/Qwen3-Embedding-0.6B'] ?? [1024]
  if (qwen === '4b') return REMOTE_MODEL_DIMENSIONS['Qwen/Qwen3-Embedding-4B'] ?? [2560]
  if (qwen === '8b') return REMOTE_MODEL_DIMENSIONS['Qwen/Qwen3-Embedding-8B'] ?? [4096]
  return [256, 384, 512, 768, 1024, 1536, 2560, 4096]
}

/** Hint for probes; null means omit `dimensions` and discover native width. */
export function probeDimensionHint(model: string): number | null {
  const exact = REMOTE_MODEL_RECOMMENDED[model]
  if (exact !== undefined) return exact
  if (/nomic-embed-text-v1\.5/i.test(model)) return 512
  const qwen = matchQwenEmbeddingFamily(model)
  if (qwen === '0.6b') return 1024
  if (qwen === '4b') return 2560
  if (qwen === '8b') return 4096
  return null
}

export function recommendedRemoteDimensions(model: string): number {
  return probeDimensionHint(model) ?? 512
}

const API_BASE_URL_EMBEDDINGS_ERROR =
  'API base URL should not include `/embeddings` — the client appends it automatically. Use e.g. `https://host/v1`.'

export function getApiBaseUrlError(value: string): string | null {
  try {
    const path = new URL(value).pathname.replace(/\/+$/, '')
    return /\/embeddings$/i.test(path) ? API_BASE_URL_EMBEDDINGS_ERROR : null
  } catch {
    return null
  }
}
